import { useMediaQuery } from "@chakra-ui/react";
import { GetProgrammaticCampaignAccountsInBuyerResearchStageRO } from "@intentsify/dto";
import { ProgrammaticCampaignAccountInBuyerResearchStage } from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import { Card, MIN_MODAL_CONTENT_HEIGHT, PieChart } from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { PieColor } from "components/Charts/PieChart/PieChart.utils";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { MdAnalytics } from "react-icons/md";
import { ChartExport, useExportableChart } from "shared/components";
import { DisplayPerformanceFilters } from "../../../../types";

type AccountsInBuyerResearchStageProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getAccountInBuyerResearchStage: (
    id: string,
    filters: DisplayPerformanceFilters
  ) => Promise<GetProgrammaticCampaignAccountsInBuyerResearchStageRO>;
};

const getBuyStageResearchColors = (
  data: ProgrammaticCampaignAccountInBuyerResearchStage[]
) => {
  const lightColors: PieColor[] = [];
  const darkColors: PieColor[] = [];
  const stageColors = {
    interest: {
      light: {
        color: "green",
        value: `var(--chakra-colors-green-300)`,
      },
      dark: {
        color: "green",
        value: `var(--chakra-colors-green-100)`,
      },
    },
    consideration: {
      light: {
        color: "blue",
        value: `var(--chakra-colors-blue-300)`,
      },
      dark: {
        color: "blue",
        value: `var(--chakra-colors-blue-100)`,
      },
    },
    awareness: {
      light: {
        color: "yellow",
        value: `var(--chakra-colors-yellow-300)`,
      },
      dark: {
        color: "yellow",
        value: `var(--chakra-colors-yellow-100)`,
      },
    },
    decision: {
      light: {
        color: "orange",
        value: `var(--chakra-colors-orange-300)`,
      },
      dark: {
        color: "orange",
        value: `var(--chakra-colors-orange-100)`,
      },
    },
  };

  data.forEach((stage) => {
    const buyerResearchStageKey =
      stage.buyerResearchStage.toLowerCase() as keyof typeof stageColors;
    const colors = stageColors[buyerResearchStageKey];

    if (colors) {
      lightColors.push(colors.light);
      darkColors.push(colors.dark);
    }
  });

  return {
    light: lightColors,
    dark: darkColors,
  };
};

const TITLE = "Accounts Reached In Buyer Research Stage";

const AccountsInBuyerResearchStage = (
  props: AccountsInBuyerResearchStageProps
) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <AccountsInBuyerResearchStageContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const AccountsInBuyerResearchStageContent = ({
  campaignId,
  filters,
  getAccountInBuyerResearchStage,
}: AccountsInBuyerResearchStageProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLargerThan1800] = useMediaQuery("(min-width: 1800px)");

  const {
    data: buyerResearchStageData,
    isFetching: isLoadingBuyerResearchStageData,
  } = useQuery(["getAccountInBuyerResearchStage", campaignId, filters], () =>
    getAccountInBuyerResearchStage(campaignId, filters)
  );

  const dates = useMemo(() => {
    return {
      start: DateTime.fromObject({
        weekNumber: filters.weekFrom,
        weekYear: filters.yearFrom,
      }).toISO(),
      end: DateTime.fromObject({
        weekNumber: filters.weekTo,
        weekYear: filters.yearTo,
      }).toISO(),
    };
  }, [filters]);

  const accountsInBuyerResearchStage = useExportableChart({
    title: TITLE,
    campaignId: Number(campaignId),
    dates,
    chart: (
      <PieChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        stretch
        isLoading={isLoadingBuyerResearchStageData}
        legendPosition={isLargerThan1800 ? "right" : "bottom"}
        data={(buyerResearchStageData?.accountsInBuyerResearchStage ?? []).map(
          (i) => ({
            value: i.countAccounts,
            label: i.buyerResearchStage,
          })
        )}
        colorSet={getBuyStageResearchColors(
          buyerResearchStageData?.accountsInBuyerResearchStage ?? []
        )}
      />
    ),
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isLoadingBuyerResearchStageData}
      title={accountsInBuyerResearchStage.title}
      noDataMessage="This visualization is designated for ad solution campaigns that are synced with an intent model."
      noDataIcon={MdAnalytics}
      hasData={!!buyerResearchStageData?.accountsInBuyerResearchStage.length}
      isExpandable
      actions={
        <ChartExport
          size="small"
          dates={dates}
          campaignId={Number(campaignId)}
          title={accountsInBuyerResearchStage.title}
          onExportPNG={accountsInBuyerResearchStage.downloadAsPng}
          data={(
            buyerResearchStageData?.accountsInBuyerResearchStage ?? []
          ).map((i) => ({
            value: i.countAccounts,
            label: i.buyerResearchStage,
          }))}
        />
      }
    >
      {accountsInBuyerResearchStage.component}
      {accountsInBuyerResearchStage.exportComponent}
    </Card>
  );
};

export { AccountsInBuyerResearchStage };
